import React, { useEffect, useState, useCallback, useMemo } from "react";
import { IHeadersDataPagination } from "interfaces/pagination";
import { IColumnMeta } from "interfaces/table";
import { Box } from "@chakra-ui/react";
import Card from "components/card/Card";
import Table from "shared/Table";
import PaginatorPages from "shared/Paginator";
import { environments } from "constants/environments";
import { useAuth } from "contexts/auth";
import { IApiMetaData } from "interfaces/apiMetaData";
import { callApi } from "apiServices/callApi";
import AlertBox from "shared/AlertBox";
import SelectButtonToggle from "shared/SelectButtonToggle";
import { IUser } from "interfaces/user";
import DialogModel from "shared/DialogModel";
import AddUser from "./components/AddUser";
import TabBar from "shared/TabBar";
import UpdateUser from "./components/UpdateUser";
import {
  dateCreatedAtBodyTemplate,
  dateUpdatedAtBodyTemplate,
} from "utils/bodyTemlates";
import { FaEye } from "react-icons/fa";

const Users = () => {
  const [data, setData] = useState<IUser[]>([]);
  const [pagination, setPagination] = useState<IHeadersDataPagination>({
    paginationcurrent: 1,
    paginationlimit: 20,
    paginationtotal: 0,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState(false);
  const { sharedData } = useAuth();
 
  const usersAPI = useMemo(
    () =>
      environments.domain + sharedData?.apisList?.Dashboard?.User?.General,
    [sharedData]
  );

  const reFetch = () => {
    getData();
  };

  useEffect(() => {
    getData();
  }, [pagination.paginationcurrent, pagination.paginationlimit]);

  const getData = useCallback(async () => {
    try {
      setLoading(true);
      setIsError(false);

      const response: IApiMetaData = await callApi(
        "GET",
        usersAPI,
        {},
        pagination,
        { target: "user", populatefields: "program", populateselected: "name" }
      );

      if (response.Status === 200) {
        setPagination((prevPagination) => ({
          ...prevPagination,
          paginationtotal: response.Pagination?.total || 0,
        }));
        setData(response.Data?.Content || []);
      } else {
        setIsError(true);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setIsError(true);
    } finally {
      setLoading(false);
    }
  }, [usersAPI, pagination]);

  const columns: IColumnMeta<IUser>[] = [
    {
      field: "name",
      header: "Name",
    },
    {
      field: "email",
      header: "Email",
    },
    {
      field: "program",
      header: "Programs",
      body: (data: IUser) => (
        <>
          {Array.isArray(data?.program) && data.program.length > 0 ? (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                {`${data.program.length} Programs `}
                <DialogModel
                  title="Programs"
                  customButton={
                    <FaEye
                      style={{
                        cursor: "pointer",
                        color: "#007bff",
                        fontSize: "1.2em",
                        display: "flex",
                      }}
                    />
                  }
                  width="40vw"
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    {data.program?.map((program: any, index: number) => (
                      <span
                        key={index}
                        style={{
                          backgroundColor: "#f5f5f5",
                          padding: "5px 10px",
                          borderRadius: "5px",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {program.name}
                      </span>
                    )) || "No Programs"}
                  </div>
                </DialogModel>
              </div>
            </>
          ) : (
            "No Programs"
          )}
        </>
      ),
    },
    {
      field: "isActive",
      header: "Status",
      body: (data: IUser) => <SelectButtonToggle data={data} />,
    },
    {
      field: "",
      header: "Update Student",
      body: (data: IUser) => (
        <DialogModel title="Update Student" label="Update" width="75vw">
          <UpdateUser rowData={data} onSave={reFetch} />
        </DialogModel>
      ),
    },
    {
      field: "createdAt",
      header: "CreatedAt",
      body: dateCreatedAtBodyTemplate,
    },
    {
      field: "updatedAt",
      header: "updatedAt",
      body: dateUpdatedAtBodyTemplate,
    },
  ];

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card className="mb-4">
        <TabBar>
          <DialogModel title="Add Student" label="Add Student" width="60vw">
            <AddUser onSave={reFetch} />
          </DialogModel>
        </TabBar>
      </Card>

      <Card className="mb-4">
        <div style={{ width: "100%", marginTop: "20px", marginBottom: "20px" }}>
          {!isError ? (
            <Box>
              <Table data={data} columns={columns} loading={loading} />
              <PaginatorPages
                pagination={pagination}
                setPagination={setPagination}
              />
            </Box>
          ) : (
            <div className="grid justify-content-center align-content-center">
              <AlertBox
                status="error"
                description="Something went wrong. Please try again later."
                title="Error"
              />
            </div>
          )}
        </div>
      </Card>
    </Box>
  );
};

export default Users;
